import type { WinStats } from '@/shared/types';
import type { FighterType } from '@/shared/constants';
import { logger } from './logger';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

/**
 * Singleton service for fetching and recording win statistics.
 * Caches the latest stats in memory to avoid redundant network calls each frame.
 */
class StatsApi {
  private cache: WinStats | null = null;
  private fetching = false;

  /** Fetch current win stats from the API. Returns cached value while a fetch is in-flight. */
  async getStats(): Promise<WinStats> {
    if (this.cache) return this.cache;
    return this.refresh();
  }

  /** Force a fresh fetch from the API. */
  async refresh(): Promise<WinStats> {
    if (this.fetching && this.cache) return this.cache;

    this.fetching = true;
    try {
      const res = await fetch(`${API_URL}/api/stats`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const stats: WinStats = await res.json();
      this.cache = stats;
      return stats;
    } catch (err) {
      logger.error('Failed to fetch stats', err);
      return this.cache ?? { trex: 0, spinosaurus: 0, elon: 0, nikita: 0 };
    } finally {
      this.fetching = false;
    }
  }

  /** Record a win and return updated stats. */
  async recordWin(winner: FighterType): Promise<WinStats> {
    try {
      const res = await fetch(`${API_URL}/api/stats/record`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ winner }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const stats: WinStats = await res.json();
      this.cache = stats;
      return stats;
    } catch (err) {
      logger.error('Failed to record win', err);
      // Optimistic local increment so the UI still updates
      if (this.cache) {
        this.cache[winner]++;
      }
      return this.cache ?? { trex: 0, spinosaurus: 0, elon: 0, nikita: 0 };
    }
  }

  /** Get the cached stats synchronously (may be null if never fetched). */
  getCached(): WinStats | null {
    return this.cache;
  }
}

export const statsApi = new StatsApi();
